import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';

/* ─── Design Tokens ────────────────────────────────────────────────── */
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyHover:   'rgba(14,34,93,0.06)',
  gold:        '#fbb21f',
  goldDim:     'rgba(251,178,31,0.14)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  bg:          '#f7f8fa',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red:         '#dc2626',
  redDim:      'rgba(220,38,38,0.08)',
  redBorder:   'rgba(220,38,38,0.25)',
};

/* ─── Types ─────────────────────────────────────────────────────────── */
interface QuestionOption {
  option_text: string;
  is_correct:  boolean;
}

interface Question {
  question_text: string;
  marks:         number;
  options:       QuestionOption[];
}

interface ClassTest {
  class_test_id:   string;
  institute_id:    string;
  institute_name:  string | null;
  branch_id:       string | null;
  branch_name:     string | null;
  batch_id:        string | null;
  batch_name:      string | null;
  title:           string;
  subject:         string;
  schedule_date:   string | null;
  schedule_time:   string | null;
  status:          'scheduled' | 'conducted' | 'cancelled';
  conducted_date:  string | null;
  questions:       Question[];
  created_at:      string | null;
  created_by:      string | null;
  created_by_name: string | null;
}

interface ApiResponse {
  success:    boolean;
  data:       ClassTest[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

/* ─── Component ─────────────────────────────────────────────────────── */
const ClassTestList: React.FC = () => {
  const [tests,         setTests]         = useState<ClassTest[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState<string | null>(null);
  const [page,          setPage]          = useState(1);
  const [limit,         setLimit]         = useState(10);
  const [pagination,    setPagination]    = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [selected,      setSelected]      = useState<ClassTest[] | null>(null);
  const [statusFilter,  setStatusFilter]  = useState('');
  const [search,        setSearch]        = useState('');
  const [searchInput,   setSearchInput]   = useState('');

  const { profile } = useOperatorProfileStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (profile?.branch_id) fetchTests();
  }, [page, limit, statusFilter, search, profile]);

  const fetchTests = async () => {
    if (!profile?.branch_id) {
      setError('Branch information not available');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (statusFilter) params.set('status', statusFilter);
      if (search)       params.set('search', search);

      const res = await apiClient.get<ApiResponse>(
        `/v1/class-tests/by-branch/${profile.branch_id}?${params}`
      );
      if (res.data.success) {
        setTests(res.data.data);
        setPagination(res.data.pagination);
      } else {
        setError('Failed to load class tests');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load class tests');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this class test?')) return;
    try {
      await apiClient.delete(`/v1/class-tests/${id}`);
      fetchTests();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleBulkDelete = async () => {
    if (!selected?.length) return;
    if (!window.confirm(`Delete ${selected.length} test(s)?`)) return;
    try {
      await Promise.all(selected.map(t => apiClient.delete(`/v1/class-tests/${t.class_test_id}`)));
      setSelected(null);
      fetchTests();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { setSearch(searchInput); setPage(1); }
  };

  /* ── Cell renderers ─────────────────────────────────────────────── */
  const statusTemplate = (row: ClassTest) => {
    const map: Record<string, { bg: string; text: string; border: string; icon: string }> = {
      scheduled: { bg: C.goldDim,  text: '#a06000',  border: C.goldBorder,  icon: 'pi-calendar-clock' },
      conducted: { bg: C.greenDim, text: '#166534',  border: C.greenBorder, icon: 'pi-check-circle'   },
      cancelled: { bg: C.redDim,   text: C.red,      border: C.redBorder,   icon: 'pi-times-circle'   },
    };
    const s = map[row.status] ?? { bg: C.navyHover, text: C.navy, border: 'rgba(14,34,93,0.2)', icon: 'pi-circle' };
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '20px', fontSize: '0.71rem', fontWeight: '700', background: s.bg, color: s.text, border: `1px solid ${s.border}`, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        <i className={`pi ${s.icon}`} style={{ fontSize: '0.68rem' }} />
        {row.status}
      </span>
    );
  };

  const titleTemplate = (row: ClassTest) => (
    <div>
      <div style={{ fontWeight: '700', color: C.navy, fontSize: '0.84rem', marginBottom: '2px' }}>{row.title}</div>
      {row.batch_name && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: C.sub, background: C.navyHover, border: `1px solid rgba(14,34,93,0.12)`, borderRadius: '12px', padding: '1px 8px' }}>
          <i className="pi pi-users" style={{ fontSize: '0.65rem' }} />
          {row.batch_name}
        </div>
      )}
    </div>
  );

  const scheduleTemplate = (row: ClassTest) => {
    if (!row.schedule_date && !row.schedule_time) return <span style={{ color: C.muted }}>—</span>;
    return (
      <div>
        {row.schedule_date && (
          <div style={{ fontSize: '0.82rem', color: C.body, fontWeight: '600' }}>
            <i className="pi pi-calendar" style={{ fontSize: '0.7rem', marginRight: '4px', color: C.muted }} />
            {row.schedule_date}
          </div>
        )}
        {row.schedule_time && (
          <div style={{ fontSize: '0.78rem', color: C.sub, marginTop: '2px' }}>
            <i className="pi pi-clock" style={{ fontSize: '0.68rem', marginRight: '4px' }} />
            {row.schedule_time}
          </div>
        )}
      </div>
    );
  };

  const questionsTemplate = (row: ClassTest) => {
    const total     = row.questions.length;
    const totalMark = row.questions.reduce((s, q) => s + q.marks, 0);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <div style={{ fontSize: '0.82rem', fontWeight: '700', color: C.navy }}>{total} Q</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: '#a06000', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '10px', padding: '1px 7px', width: 'fit-content' }}>
          <i className="pi pi-star" style={{ fontSize: '0.62rem' }} />
          {totalMark} pts
        </div>
      </div>
    );
  };

  const actionTemplate = (row: ClassTest) => (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
      <button
        onClick={() => navigate(`/exams/class-tests/${row.class_test_id}`)}
        title="View Details"
        style={{
          padding: '6px 12px',
          height: '32px',
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: '8px',
          cursor: 'pointer',
          color: C.muted,
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          fontSize: '0.75rem',
          fontWeight: '600',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(14,34,93,0.08)';
          e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)';
          e.currentTarget.style.color = C.navy;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = C.border;
          e.currentTarget.style.color = C.muted;
        }}
      >
        <i className="pi pi-eye" style={{ fontSize: '0.8rem' }} />
        View
      </button>

      <button
        onClick={() => navigate(`/exams/class-tests/${row.class_test_id}/edit`)}
        title="Edit Class Test"
        style={{
          padding: '6px 12px',
          height: '32px',
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: '8px',
          cursor: 'pointer',
          color: C.muted,
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          fontSize: '0.75rem',
          fontWeight: '600',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(251,178,31,0.12)';
          e.currentTarget.style.borderColor = 'rgba(251,178,31,0.35)';
          e.currentTarget.style.color = '#b8860b';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = C.border;
          e.currentTarget.style.color = C.muted;
        }}
      >
        <i className="pi pi-pencil" style={{ fontSize: '0.8rem' }} />
        Edit
      </button>

      <button
        onClick={() => handleDelete(row.class_test_id)}
        title="Delete Class Test"
        style={{
          padding: '6px 12px',
          height: '32px',
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: '8px',
          cursor: 'pointer',
          color: C.muted,
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          fontSize: '0.75rem',
          fontWeight: '600',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(220,38,38,0.08)';
          e.currentTarget.style.borderColor = 'rgba(220,38,38,0.25)';
          e.currentTarget.style.color = C.red;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = C.border;
          e.currentTarget.style.color = C.muted;
        }}
      >
        <i className="pi pi-trash" style={{ fontSize: '0.8rem' }} />
        Delete
      </button>
    </div>
  );

  /* ── Pagination ─────────────────────────────────────────────────── */
  const renderPagination = () => {
    const total = pagination.pages;
    const pages: (number | string)[] = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, '...', total);
    } else if (page >= total - 2) {
      pages.push(1, '...', total - 2, total - 1, total);
    } else {
      pages.push(1, '...', page, '...', total);
    }
    return pages.map((p, i) =>
      p === '...' ? (
        <span key={`e${i}`} style={{ padding: '0 4px', color: C.muted, fontSize: '0.82rem' }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => setPage(p as number)}
          style={{ minWidth: '32px', height: '32px', padding: '0 8px', borderRadius: '7px', fontSize: '0.82rem', fontWeight: page === p ? '700' : '500', background: page === p ? C.navy : 'transparent', color: page === p ? C.gold : C.navy, border: page === p ? 'none' : `1px solid ${C.border}`, boxShadow: page === p ? '0 2px 6px rgba(14,34,93,0.25)' : 'none', cursor: 'pointer', transition: 'all 0.15s ease' }}
          onMouseEnter={e => { if (page !== p) e.currentTarget.style.background = C.navyHover; }}
          onMouseLeave={e => { if (page !== p) e.currentTarget.style.background = 'transparent'; }}
        >
          {p}
        </button>
      )
    );
  };

  /* ── Loading skeleton ───────────────────────────────────────────── */
  if (loading && tests.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading class tests…</p>
        </div>
      </div>
    );
  }

  const sel = selected ?? [];

  return (
    <>
      <style>{`
        .ct-datatable .p-datatable-thead > tr > th {
          background: #fafafa !important;
          color: ${C.muted} !important;
          font-size: 0.7rem !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          border-bottom: 2px solid ${C.border} !important;
          padding: 12px 14px !important;
        }
        .ct-datatable .p-datatable-tbody > tr { transition: background 0.12s; }
        .ct-datatable .p-datatable-tbody > tr:hover > td { background: rgba(14,34,93,0.025) !important; }
        .ct-datatable .p-datatable-tbody > tr > td {
          border-bottom: 1px solid #f3f4f6 !important;
          padding: 10px 12px !important;
          font-size: 0.83rem;
        }
        .ct-datatable .p-checkbox .p-checkbox-box.p-highlight { background: ${C.navy} !important; border-color: ${C.navy} !important; }
        .ct-datatable .p-checkbox .p-checkbox-box:hover { border-color: ${C.gold} !important; }
        .ct-filter-btn { transition: all 0.15s; }
        .ct-filter-btn:hover { background: ${C.navyHover} !important; border-color: rgba(14,34,93,0.2) !important; color: ${C.navy} !important; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.88rem' }} />}

        <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden' }}>

          {/* ── Toolbar ─────────────────────────────────────────────── */}
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>

            {/* Left: rows + status filter + search */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.8rem', color: C.sub }}>Showing</span>
              <select
                value={limit}
                onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                style={{ padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '0.8rem', color: C.navy, background: C.white, fontWeight: '600', cursor: 'pointer', outline: 'none' }}
              >
                {[5, 10, 20, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>

              {/* Status filter pills */}
              <div style={{ display: 'flex', gap: '4px', marginLeft: '4px' }}>
                {(['', 'scheduled', 'conducted', 'cancelled'] as const).map(s => {
                  const active  = statusFilter === s;
                  const labels  = { '': 'All', scheduled: 'Scheduled', conducted: 'Conducted', cancelled: 'Cancelled' };
                  return (
                    <button
                      key={s}
                      className="ct-filter-btn"
                      onClick={() => { setStatusFilter(s); setPage(1); }}
                      style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer', border: active ? 'none' : `1px solid ${C.border}`, background: active ? C.navy : 'transparent', color: active ? C.gold : C.sub, boxShadow: active ? '0 2px 6px rgba(14,34,93,0.2)' : 'none', transition: 'all 0.15s' }}
                    >
                      {labels[s]}
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div style={{ position: 'relative' }}>
                <i className="pi pi-search" style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.72rem', color: C.muted }} />
                <input
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchSubmit}
                  placeholder="Search title… (Enter)"
                  style={{ paddingLeft: '28px', paddingRight: '10px', paddingTop: '5px', paddingBottom: '5px', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '0.78rem', color: C.body, outline: 'none', width: '180px', fontFamily: 'inherit' }}
                />
                {search && (
                  <button
                    onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                    style={{ position: 'absolute', right: '7px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '0.72rem', padding: 0 }}
                  >
                    <i className="pi pi-times" />
                  </button>
                )}
              </div>
            </div>

            {/* Right: bulk delete */}
            {sel.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: '#f8f9ff', border: `1px solid ${C.border}`, borderRadius: '10px' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: '700', color: C.navy }}>{sel.length} Selected</span>
                <button
                  onClick={handleBulkDelete}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 12px', background: C.redDim, border: `1px solid ${C.redBorder}`, borderRadius: '7px', color: C.red, fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer' }}
                >
                  <i className="pi pi-trash" style={{ fontSize: '0.72rem' }} />
                  Delete
                </button>
              </div>
            )}
          </div>

          {/* ── DataTable ───────────────────────────────────────────── */}
          <DataTable
            value={tests}
            selection={selected}
            onSelectionChange={(e: any) => setSelected(e.value)}
            dataKey="class_test_id"
            loading={loading}
            emptyMessage={
              <div style={{ textAlign: 'center', padding: '40px 20px', color: C.muted }}>
                <i className="pi pi-book" style={{ fontSize: '2rem', display: 'block', marginBottom: '10px', opacity: 0.4 }} />
                <span style={{ fontSize: '0.9rem' }}>No class tests found</span>
              </div>
            }
            className="ct-datatable"
            style={{ fontSize: '0.83rem' }}
          >
            <Column selectionMode="multiple" headerStyle={{ width: '3rem', padding: '12px 14px' }} bodyStyle={{ padding: '12px 14px' }} />
            <Column header="Title / Batch"    body={titleTemplate}    headerStyle={{ minWidth: '240px' }} />
            <Column header="Schedule"         body={scheduleTemplate} headerStyle={{ minWidth: '140px' }} />
            <Column header="Questions / Marks" body={questionsTemplate} headerStyle={{ minWidth: '120px' }} />
            <Column header="Status"           body={statusTemplate}   headerStyle={{ minWidth: '130px' }} />
            <Column
              header="Created By"
              body={(row: ClassTest) => (
                <span style={{ color: C.sub, fontSize: '0.82rem' }}>
                  {row.created_by_name ?? '—'}
                </span>
              )}
              headerStyle={{ minWidth: '130px' }}
            />
            <Column header="Actions" body={actionTemplate} headerStyle={{ minWidth: '260px', textAlign: 'center' }} bodyStyle={{ textAlign: 'center' }} />
          </DataTable>

          {/* ── Pagination footer ───────────────────────────────────── */}
          {pagination.pages > 0 && (
            <div style={{ padding: '14px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <span style={{ fontSize: '0.8rem', color: C.muted }}>
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} tests
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <button onClick={() => setPage(1)} disabled={page === 1} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-double-left" style={{ fontSize: '0.7rem' }} />
                </button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-left" style={{ fontSize: '0.7rem' }} />
                </button>

                {renderPagination()}

                <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-right" style={{ fontSize: '0.7rem' }} />
                </button>
                <button onClick={() => setPage(pagination.pages)} disabled={page === pagination.pages} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-double-right" style={{ fontSize: '0.7rem' }} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ClassTestList;